/**
 * Staff Management Interface
 * CRUD operations, availability, and leave management
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Plus,
  Edit,
  Calendar,
  UserCheck,
  UserX,
  Clock,
  Award,
  Trash2,
  ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  useStaff,
  useCreateStaff,
  useUpdateStaff,
  useStaffLeave,
  useAddLeave,
  useUpdateLeave,
  useDeleteLeave
} from '../hooks/use-scheduling';

const JOB_ROLES = [
  { value: 'lead_installer', label: 'Lead Installer', color: 'blue' },
  { value: 'electrician_mate', label: 'Electrician Mate (Electrician 2)', color: 'purple' },
  { value: 'electrician_labourer', label: 'Electrician Labourer', color: 'indigo' },
  { value: 'lead_roofer', label: 'Lead Roofer', color: 'green' },
  { value: 'roofer_2', label: 'Roofer 2', color: 'teal' },
  { value: 'labourer', label: 'Labourer', color: 'gray' }
];

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contractor', label: 'Contractor' }
];

export default function StaffManagement() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [viewStaff, setViewStaff] = useState<any>(null);

  const { data: staff = [], isLoading } = useStaff();
  const createStaff = useCreateStaff();
  const updateStaff = useUpdateStaff();

  const getRoleColor = (role: string) => {
    return JOB_ROLES.find(r => r.value === role)?.color || 'gray';
  };

  const handleCreateStaff = async (formData: any) => {
    await createStaff.mutateAsync(formData);
    setIsCreateOpen(false);
  };

  // If viewing staff details
  if (viewStaff) {
    return <StaffDetailView staff={viewStaff} onBack={() => setViewStaff(null)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-white/20 backdrop-blur-xl bg-white/40 dark:bg-slate-900/40 sticky top-0 z-50"
      >
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                Staff Management
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                Manage team members, roles, and availability
              </p>
            </div>

            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Staff
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add New Staff Member</DialogTitle>
                </DialogHeader>
                <StaffForm onSubmit={handleCreateStaff} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <StatsCard
            title="Total Staff"
            value={staff.length}
            icon={Users}
            color="blue"
          />
          <StatsCard
            title="Active"
            value={staff.filter((s: any) => s.isActive).length}
            icon={UserCheck}
            color="green"
          />
          <StatsCard
            title="Full Time"
            value={staff.filter((s: any) => s.employmentType === 'full_time').length}
            icon={Clock}
            color="purple"
          />
          <StatsCard
            title="Contractors"
            value={staff.filter((s: any) => s.employmentType === 'contractor').length}
            icon={Award}
            color="indigo"
          />
        </div>

        {/* Staff List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
          </div>
        ) : staff.length === 0 ? (
          <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                No staff members yet
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Add your first staff member to get started
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {staff.map((member: any, index: number) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <StaffCard
                  staff={member}
                  getRoleColor={getRoleColor}
                  onView={(s) => setViewStaff(s)}
                  onEdit={(s) => setSelectedStaff(s)}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!selectedStaff} onOpenChange={() => setSelectedStaff(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Staff Member</DialogTitle>
          </DialogHeader>
          {selectedStaff && (
            <StaffForm
              staff={selectedStaff}
              onSubmit={async (data) => {
                await updateStaff.mutateAsync({ id: selectedStaff.id, data });
                setSelectedStaff(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Stats Card Component
function StatsCard({
  title,
  value,
  icon: Icon,
  color
}: {
  title: string;
  value: number;
  icon: any;
  color: string;
}) {
  return (
    <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 hover:scale-105 transition-transform duration-300">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
          {title}
        </CardTitle>
        <div className={`p-2 rounded-lg bg-${color}-500/10`}>
          <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-bold bg-gradient-to-r from-${color}-600 to-${color}-400 bg-clip-text text-transparent`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

// Staff Card Component
function StaffCard({
  staff,
  getRoleColor,
  onView,
  onEdit
}: {
  staff: any;
  getRoleColor: (role: string) => string;
  onView: (staff: any) => void;
  onEdit: (staff: any) => void;
}) {
  const roleColor = getRoleColor(staff.jobRole);
  const roleName = JOB_ROLES.find(r => r.value === staff.jobRole)?.label || staff.jobRole;

  return (
    <Card
      className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 hover:shadow-2xl transition-all duration-300 cursor-pointer"
      onClick={() => onView(staff)}
    >
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-r from-${roleColor}-500 to-${roleColor}-400 flex items-center justify-center text-white text-lg font-bold`}>
              {staff.user?.fullName?.[0] || staff.user?.username?.[0] || 'S'}
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                {staff.user?.fullName || staff.user?.username || 'Unknown'}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {staff.user?.email || 'No email'}
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(staff);
            }}
            className="h-8 w-8"
          >
            <Edit className="w-4 h-4" />
          </Button>
        </div>

        {/* Role Badge */}
        <Badge className={`bg-${roleColor}-500/10 text-${roleColor}-700 dark:text-${roleColor}-300 mb-3`}>
          {roleName}
        </Badge>

        {/* Details */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Employment:</span>
            <span className="font-medium capitalize">
              {staff.employmentType.replace('_', ' ')}
            </span>
          </div>


          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Max Jobs/Day:</span>
            <span className="font-medium">{staff.maxJobsPerDay}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Status:</span>
            <Badge variant={staff.isActive ? 'success' : 'secondary'}>
              {staff.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>

        {/* Skills */}
        {staff.skills && staff.skills.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-slate-600 dark:text-slate-400 mb-2">Skills:</div>
            <div className="flex flex-wrap gap-1">
              {staff.skills.slice(0, 3).map((skill: string, i: number) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {skill}
                </Badge>
              ))}
              {staff.skills.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{staff.skills.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Staff Form Component
function StaffForm({
  staff,
  onSubmit
}: {
  staff?: any;
  onSubmit: (data: any) => void;
}) {
  const [formData, setFormData] = useState({
    userId: staff?.userId || '',
    employmentType: staff?.employmentType || 'full_time',
    jobRole: staff?.jobRole || 'labourer',
    email: staff?.email || '',
    phoneNumber: staff?.phoneNumber || '',
    maxJobsPerDay: staff?.maxJobsPerDay || 1,
    emailNotifications: staff?.emailNotifications !== undefined ? staff.emailNotifications : true,
    smsNotifications: staff?.smsNotifications !== undefined ? staff.smsNotifications : false,
    requiresSupervision: staff?.requiresSupervision || false,
    isActive: staff?.isActive !== undefined ? staff.isActive : true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>User ID</Label>
          <Input
            type="number"
            value={formData.userId}
            onChange={(e) => setFormData({ ...formData, userId: parseInt(e.target.value) })}
            required
          />
        </div>

        <div>
          <Label>Employment Type</Label>
          <Select
            value={formData.employmentType}
            onValueChange={(value) => setFormData({ ...formData, employmentType: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EMPLOYMENT_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2">
          <Label>Job Role</Label>
          <Select
            value={formData.jobRole}
            onValueChange={(value) => setFormData({ ...formData, jobRole: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {JOB_ROLES.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Email</Label>
          <Input
            type="email"
            placeholder="staff@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <p className="text-xs text-muted-foreground mt-1">For email notifications and contact</p>
        </div>

        <div>
          <Label>Phone Number</Label>
          <Input
            type="tel"
            placeholder="+44 7XXX XXXXXX"
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
          />
          <p className="text-xs text-muted-foreground mt-1">For SMS notifications and team contact</p>
        </div>

        <div>
          <Label>Max Jobs Per Day</Label>
          <Input
            type="number"
            min="1"
            value={formData.maxJobsPerDay}
            onChange={(e) => setFormData({ ...formData, maxJobsPerDay: parseInt(e.target.value) })}
          />
        </div>
      </div>

      <div className="space-y-3 border-t pt-4">
        <Label className="text-sm font-medium">Notifications</Label>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="emailNotifications"
            checked={formData.emailNotifications}
            onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
            className="rounded border-gray-300"
          />
          <label htmlFor="emailNotifications" className="text-sm">
            Email notifications for schedule changes
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="smsNotifications"
            checked={formData.smsNotifications}
            onChange={(e) => setFormData({ ...formData, smsNotifications: e.target.checked })}
            className="rounded border-gray-300"
          />
          <label htmlFor="smsNotifications" className="text-sm">
            SMS notifications for schedule changes
          </label>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4">
        <Button type="submit" className="bg-gradient-to-r from-blue-600 to-indigo-600">
          {staff ? 'Update' : 'Create'} Staff
        </Button>
      </div>
    </form>
  );
}

// Staff Detail View with Annual Leave Tab
function StaffDetailView({ staff, onBack }: { staff: any; onBack: () => void }) {
  const [isAddLeaveOpen, setIsAddLeaveOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState<any>(null);

  const { data: leaveRecords = [], isLoading: leaveLoading } = useStaffLeave(staff.id);
  const addLeave = useAddLeave();
  const updateLeave = useUpdateLeave();
  const deleteLeave = useDeleteLeave();

  const isFullTime = staff.employmentType === 'full_time';
  const roleColor = JOB_ROLES.find(r => r.value === staff.jobRole)?.color || 'gray';
  const roleName = JOB_ROLES.find(r => r.value === staff.jobRole)?.label || staff.jobRole;

  const handleAddLeave = async (formData: any) => {
    await addLeave.mutateAsync({ staffId: staff.id, data: formData });
    setIsAddLeaveOpen(false);
  };

  const handleUpdateLeave = async (formData: any) => {
    await updateLeave.mutateAsync({ leaveId: editingLeave.id, data: formData });
    setEditingLeave(null);
  };

  const handleDeleteLeave = async (leaveId: number) => {
    if (confirm('Are you sure you want to delete this leave record?')) {
      await deleteLeave.mutateAsync(leaveId);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-white/20 backdrop-blur-xl bg-white/40 dark:bg-slate-900/40 sticky top-0 z-50"
      >
        <div className="container mx-auto p-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3 flex-1">
              <div className={`w-16 h-16 rounded-full bg-gradient-to-r from-${roleColor}-500 to-${roleColor}-400 flex items-center justify-center text-white text-2xl font-bold`}>
                {staff.user?.fullName?.[0] || staff.user?.username?.[0] || 'S'}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                  {staff.user?.fullName || staff.user?.username || 'Unknown'}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`bg-${roleColor}-500/10 text-${roleColor}-700 dark:text-${roleColor}-300`}>
                    {roleName}
                  </Badge>
                  <Badge variant={staff.isActive ? 'success' : 'secondary'}>
                    {staff.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="container mx-auto p-6">
        <Tabs defaultValue={isFullTime ? 'leave' : 'info'} className="w-full">
          <TabsList className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm">
            <TabsTrigger value="info">Details</TabsTrigger>
            {isFullTime && (
              <TabsTrigger value="leave">
                <Calendar className="w-4 h-4 mr-2" />
                Annual Leave
              </TabsTrigger>
            )}
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="info" className="mt-6">
            <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60">
              <CardHeader>
                <CardTitle>Staff Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-600 dark:text-slate-400">Email</Label>
                    <p className="text-slate-900 dark:text-slate-100 mt-1">
                      {staff.user?.email || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-600 dark:text-slate-400">Employment Type</Label>
                    <p className="text-slate-900 dark:text-slate-100 mt-1 capitalize">
                      {staff.employmentType.replace('_', ' ')}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-600 dark:text-slate-400">Max Jobs Per Day</Label>
                    <p className="text-slate-900 dark:text-slate-100 mt-1">{staff.maxJobsPerDay}</p>
                  </div>
                </div>

                {staff.skills && staff.skills.length > 0 && (
                  <div>
                    <Label className="text-slate-600 dark:text-slate-400">Skills</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {staff.skills.map((skill: string, i: number) => (
                        <Badge key={i} variant="outline">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Annual Leave Tab (Full-Time Only) */}
          {isFullTime && (
            <TabsContent value="leave" className="mt-6">
              <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Annual Leave Records</CardTitle>
                  <Dialog open={isAddLeaveOpen} onOpenChange={setIsAddLeaveOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-blue-600 to-indigo-600">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Annual Leave
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Annual Leave</DialogTitle>
                      </DialogHeader>
                      <LeaveForm onSubmit={handleAddLeave} />
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {leaveLoading ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
                    </div>
                  ) : leaveRecords.length === 0 ? (
                    <div className="text-center py-8 text-slate-600 dark:text-slate-400">
                      <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                      <p>No annual leave records yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {leaveRecords.map((leave: any) => (
                        <div
                          key={leave.id}
                          className="flex items-center justify-between p-4 rounded-lg bg-white/40 dark:bg-slate-800/40"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={leave.leaveType === 'annual' ? 'default' : 'secondary'}>
                                {leave.leaveType}
                              </Badge>
                              <span className="text-sm font-medium">
                                {new Date(leave.startDate).toLocaleDateString()} -{' '}
                                {new Date(leave.endDate).toLocaleDateString()}
                              </span>
                            </div>
                            {leave.notes && (
                              <p className="text-sm text-slate-600 dark:text-slate-400">{leave.notes}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingLeave(leave)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteLeave(leave.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Edit Leave Dialog */}
      <Dialog open={!!editingLeave} onOpenChange={() => setEditingLeave(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Annual Leave</DialogTitle>
          </DialogHeader>
          {editingLeave && (
            <LeaveForm leave={editingLeave} onSubmit={handleUpdateLeave} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Leave Form Component
function LeaveForm({
  leave,
  onSubmit
}: {
  leave?: any;
  onSubmit: (data: any) => void;
}) {
  const [formData, setFormData] = useState({
    startDate: leave?.startDate?.split('T')[0] || '',
    endDate: leave?.endDate?.split('T')[0] || '',
    leaveType: leave?.leaveType || 'annual',
    notes: leave?.notes || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Start Date</Label>
          <Input
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            required
          />
        </div>
        <div>
          <Label>End Date</Label>
          <Input
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            required
          />
        </div>
      </div>

      <div>
        <Label>Leave Type</Label>
        <Select
          value={formData.leaveType}
          onValueChange={(value) => setFormData({ ...formData, leaveType: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="annual">Annual Leave</SelectItem>
            <SelectItem value="sick">Sick Leave</SelectItem>
            <SelectItem value="unpaid">Unpaid Leave</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Notes (Optional)</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Add any additional notes..."
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="submit" className="bg-gradient-to-r from-blue-600 to-indigo-600">
          {leave ? 'Update' : 'Add'} Leave
        </Button>
      </div>
    </form>
  );
}
